import { Statuses } from '@context/global';
import {
  AUTOFULFILLMENT_MODES,
  FLAT_RATE_MODES,
  INVOICE_FREQUENCYS,
  LOCALES,
  MerchantAdmin,
  PAYOR_MODES,
  PLATFORMS,
  PRICING_MODES,
  SETUP_STEPS,
  UNIT_SYSTEMS
} from '@ecocart/entities';
import { useMerchantStatusConfigs } from '@hooks/useMerchantStatusConfigs';
import { useQuery } from '@tanstack/react-query';
import { getEmissionFactors } from '@utils/api/manufacturing';
import { getPaymentMethods, hasAtLeastOnePaymentMethod, hasAtLeastOneValidPaymentMethod } from '@utils/api/payments';
import { ValidationFieldNames } from '@utils/validation';
import { FormFieldsConfig } from '../shared/EcoForm';

// TODO: Evaluation adding back 'flatRateCartAmount' to MerchantSettingsValidationFieldNames
// TODO: Context - we have grandfathered in deals that are beyond current min/max, so disable for now
// TODO: Note: validation still exists on merchant's side aka EditEcoCartSettingsForm
export const MerchantSettingsValidationFieldNames: ValidationFieldNames[] = [
  'defaultItemWeightKg',
  'ecoIncentivesCartValueThreshold',
  'ecoIncentivesNumberOfTrees'
];

export function useFormFieldsConfig(merchantAdmin: MerchantAdmin): FormFieldsConfig[] {
  const { shopName, ecocartActive, variantIds } = merchantAdmin;
  const { data: paymentMethods } = useQuery(['getPaymentMethods', shopName], () => getPaymentMethods(shopName));
  const { data: emissionFactors } = useQuery(['getEmissionFactors'], () => getEmissionFactors());

  const statuses: Statuses = {
    areVariantsCreated: !!variantIds?.[0],
    hasAValidPaymentMethod: hasAtLeastOneValidPaymentMethod(paymentMethods),
    isBillingEnabled: hasAtLeastOnePaymentMethod(paymentMethods),
    isOnboardingComplete: merchantAdmin.isOnboardingComplete
  };
  const isEcocartActiveDisabled = !ecocartActive && (!statuses.areVariantsCreated || !statuses.isBillingEnabled);
  const storeTypes = (emissionFactors || []).map((item) => item.slug);
  const { getFormTooltipConfig } = useMerchantStatusConfigs({ merchantAdmin, statuses });

  const formFieldsConfig: FormFieldsConfig<Partial<MerchantAdmin>>[] = [
    {
      field: 'ecocartActive',
      label: 'Enable EcoCart',
      type: 'toggle',
      disabled: isEcocartActiveDisabled,
      tooltipConfig: { ...getFormTooltipConfig('ecocart_active'), placement: 'left' }
    },
    {
      field: 'ecocartEnabled',
      label: 'Show Widgets on Store',
      type: 'toggle',
      disabled: !ecocartActive,
      tooltipConfig: { ...getFormTooltipConfig('widgets_visible'), placement: 'left' }
    },
    {
      field: 'companyName'
    },
    {
      field: 'ecoCalculationApiKey',
      disabled: true
    },
    {
      field: 'accessToken',
      disabled: true
    },
    {
      field: 'shopifyAccessTokenIsValid',
      disabled: true,
      type: 'checkbox'
    },
    {
      field: 'shopifyStorefrontAccessToken'
    },
    {
      field: 'rechargeAccessToken'
    },
    {
      field: 'rechargeProductId'
    },
    {
      field: 'defaultItemWeightKg'
    },
    {
      field: 'ecoIncentivesEnabled',
      type: 'checkbox'
    },
    {
      field: 'ecoIncentivesCartValueThreshold'
    },
    {
      field: 'ecoIncentivesNumberOfTrees'
    },
    {
      field: 'offsetMultiplier',
      type: 'picker',
      items: [
        {
          label: '1x - Carbon Neutral',
          value: 1
        },
        {
          label: '2x - Climate Positive',
          value: 2
        }
      ]
    },
    {
      field: 'pricingMode',
      type: 'picker',
      items: PRICING_MODES
    },
    {
      field: 'flatRateMode',
      type: 'picker',
      items: FLAT_RATE_MODES
    },
    {
      field: 'flatRatePercentage',
      label: 'Flat Rate Percentage (only relevant when "Flat Rate Mode" is "percentage")'
    },
    {
      field: 'flatRateLineItemAmount',
      label: 'Flat Rate Line Item Amount (only relevant when "Flat Rate Mode" is "line_item_basis")'
    },
    {
      field: 'flatRateCartAmount',
      label: 'Flat Rate Cart Amount (only relevant when "Flat Rate Mode" is "cart_basis")'
    },
    {
      field: 'isTestStore',
      type: 'checkbox'
    },
    {
      field: 'isBillingDisabled',
      disabled: true,
      type: 'checkbox'
    },
    {
      field: 'autofulfillmentMode',
      type: 'picker',
      items: AUTOFULFILLMENT_MODES
    },
    {
      field: 'enableManufacturing',
      type: 'checkbox',
      disabled: true
    },
    {
      field: 'enableShipping',
      type: 'checkbox',
      disabled: true
    },
    {
      field: 'platform',
      type: 'picker',
      items: PLATFORMS
    },
    {
      field: 'payorMode',
      type: 'picker',
      items: PAYOR_MODES
    },
    {
      field: 'optOut',
      type: 'checkbox'
    },
    {
      field: 'locale',
      type: 'picker',
      items: LOCALES
    },
    {
      field: 'unitSystem',
      type: 'picker',
      items: UNIT_SYSTEMS
    },
    {
      field: 'setupStep',
      type: 'picker',
      items: SETUP_STEPS
    },
    {
      field: 'invoiceFrequency',
      type: 'picker',
      items: INVOICE_FREQUENCYS
    },
    {
      field: 'siteUrl'
    },
    {
      field: 'logoUrl'
    }
  ];

  return formFieldsConfig;
}
